import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Body, Mini } from "../../styles/stringStyles";
import { hexWithOpacity } from "../../utils/hexWithOpacity";
import { TextInput } from "./TextInput";

type Props = {
  label?: string;
  date?: string;
  onChange?: (date: string) => void;
  error?: string;
  withoutError?: boolean;
  tag?: string;
};

const isComplete = (day: string, month: string, year: string) =>
  day !== "" && month !== "" && year.length === 4;

/** The ISO string for a real date, or null while the boxes are incomplete or describe a date that does not exist. */
const parseDate = (day: string, month: string, year: string): string | null => {
  if (!isComplete(day, month, year)) return null;

  const normalizedDay = day.padStart(2, "0");
  const normalizedMonth = month.padStart(2, "0");
  const parsedDay = Number(normalizedDay);
  const parsedMonth = Number(normalizedMonth);

  if (parsedDay < 1 || parsedDay > 31 || parsedMonth < 1 || parsedMonth > 12) {
    return null;
  }

  const parsedDate = new Date(
    `${year}-${normalizedMonth}-${normalizedDay}T00:00:00Z`,
  );

  // Guard against invalid rollovers (e.g. 31.02 -> 03.03).
  if (
    parsedDate.getUTCFullYear() !== Number(year) ||
    parsedDate.getUTCMonth() + 1 !== parsedMonth ||
    parsedDate.getUTCDate() !== parsedDay
  ) {
    return null;
  }

  return parsedDate.toISOString();
};

export const DatePickerText = ({
  date,
  onChange,
  label,
  error,
  withoutError,
  tag,
}: Props) => {
  const { t } = useTranslation();
  const [day, setDay] = useState(
    date ? new Date(date).getDate().toString() : "",
  );
  const [month, setMonth] = useState(
    date ? (new Date(date).getMonth() + 1).toString() : "",
  );
  const [year, setYear] = useState(
    date ? new Date(date).getFullYear().toString() : "",
  );

  // Skips the value this component just reported. Clearing the parent on an invalid edit would
  // otherwise come straight back as an empty `date` and wipe the digits still being corrected.
  const reported = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (date === reported.current) return;
    if (date) {
      const parsedDate = new Date(date);
      setDay(parsedDate.getDate().toString().padStart(2, "0"));
      setMonth((parsedDate.getMonth() + 1).toString().padStart(2, "0"));
      setYear(parsedDate.getFullYear().toString());
    } else {
      setDay("");
      setMonth("");
      setYear("");
    }
  }, [date]);

  const monthRef = useRef<HTMLInputElement>(null);
  const yearRef = useRef<HTMLInputElement>(null);

  // Reports every edit, not just the valid ones, so the value the parent holds is always the one on
  // screen — breaking a date that was already valid must not leave the previous date behind.
  const handleDateChange = (
    newDay: string,
    newMonth: string,
    newYear: string,
  ) => {
    const next = parseDate(newDay, newMonth, newYear) ?? "";
    if (next === reported.current) return;
    reported.current = next;
    onChange?.(next);
  };

  const handleDayChange = (value: string) => {
    const onlyNumbers = value.replace(/[^0-9]/g, "").slice(0, 2);
    const numberValue = Number(onlyNumbers);
    if (onlyNumbers === "" || numberValue <= 31) {
      setDay(onlyNumbers);
      handleDateChange(onlyNumbers, month, year);
    }
    if (onlyNumbers.length === 2) {
      monthRef.current?.focus();
    }
  };

  const handleMonthChange = (value: string) => {
    const onlyNumbers = value.replace(/[^0-9]/g, "").slice(0, 2);
    const numberValue = Number(onlyNumbers);
    if (onlyNumbers === "" || numberValue <= 12) {
      setMonth(onlyNumbers);
      handleDateChange(day, onlyNumbers, year);
    }
    if (onlyNumbers.length === 2) {
      yearRef.current?.focus();
    }
  };

  const handleYearChange = (value: string) => {
    const onlyNumbers = value.replace(/[^0-9]/g, "").slice(0, 4);
    setYear(onlyNumbers);
    handleDateChange(day, month, onlyNumbers);
  };

  const invalidDate =
    isComplete(day, month, year) && parseDate(day, month, year) === null
      ? t("INVALID_DATE")
      : " ";

  return (
    <Container>
      <LabelContainer>
        {label && <StyledMini>{label}</StyledMini>}
        {tag && <TagValue>{tag}</TagValue>}
      </LabelContainer>
      <InputContainer>
        <StyledTextInput
          value={day}
          onChange={handleDayChange}
          placeholder="Tag"
          inputMode="numeric"
          withoutError
        />
        <StyledTextInput
          ref={monthRef}
          value={month}
          onChange={handleMonthChange}
          placeholder="Monat"
          inputMode="numeric"
          withoutError
        />
        <StyledTextInput
          ref={yearRef}
          value={year}
          onChange={handleYearChange}
          placeholder="Jahr"
          inputMode="numeric"
          withoutError
        />
      </InputContainer>
      {!withoutError && (
        <Error>
          <ErrorText>{error ?? invalidDate}</ErrorText>
        </Error>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0px;
  width: 100%;
`;

const StyledMini = styled(Body)`
  color: ${({ theme }) => hexWithOpacity(theme.colors.dark, 0.6)};
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  width: 100%;
  flex-wrap: wrap;
`;

const ErrorText = styled(Mini)`
  color: ${({ theme }) => theme.colors.lightRed};
`;

const Error = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  padding-left: 5px;
  padding-right: 10px;
  max-lines: 1;
  height: 20px;
`;

const TagValue = styled(Mini)`
  background-color: ${({ theme }) => hexWithOpacity(theme.colors.medium, 0.1)};
  color: ${({ theme }) => hexWithOpacity(theme.colors.dark, 0.6)};
  border-radius: 5px;
  padding: 2px 6px;
`;

const LabelContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 10px;
  padding-bottom: 5px;
`;

const StyledTextInput = styled(TextInput)`
  width: 100%;
  max-width: 82px;
`;
